import PageAnimations from "animations";

const STORAGE_KEY = "theme:recently-viewed";
const CARDS_SECTION_ID = "recently-viewed-product-card";
const MAX_HANDLES = 20;

function readHandles() {
    try {
        const handles = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        return Array.isArray(handles) ? handles.filter((handle => typeof handle === "string")) : [];
    } catch (e) {
        return [];
    }
}

function clearHandles() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
}

// Removes deleted or unpublished products so they are not requested again.
function pruneHandles(deadHandles) {
    if (!deadHandles.length) return;
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(readHandles().filter((handle => !deadHandles.includes(handle)))));
    } catch (e) {}
}

// Recently viewed is preferences storage: only use it once the visitor's consent allows it.
// Resolves true/false from the customer privacy API, or null when the API could not be loaded
// (so a transient failure is not mistaken for a refusal).
function preferencesAllowed() {
    return new Promise((resolve => {
        const shopify = window.Shopify;
        if (!shopify || typeof shopify.loadFeatures !== "function") return resolve(null);
        shopify.loadFeatures([ {
            name: "consent-tracking-api",
            version: "0.1"
        } ], (error => {
            const privacy = shopify.customerPrivacy;
            if (error || typeof privacy?.preferencesProcessingAllowed !== "function") return resolve(null);
            resolve(Boolean(privacy.preferencesProcessingAllowed()));
        }));
    }));
}

class RecentlyViewedProducts extends HTMLElement {
    #observer = null;
    #inView = false;
    #loading = false;
    #recheck = false;
    #onConsentCollected = () => {
        if (!this.#inView) return;
        // A load already in flight may have read the old consent state: check again once it finishes.
        if (this.#loading) {
            this.#recheck = true;
            return;
        }
        this.#load();
    };
    connectedCallback() {
        if (this.dataset.loaded) return;
        // Consent can be given after the page loads (cookie banner), so retry when it is collected.
        document.addEventListener("visitorConsentCollected", this.#onConsentCollected);
        if (this.#inView || this.#observer) return;
        this.#observer = new IntersectionObserver((entries => {
            if (!entries.some((entry => entry.isIntersecting))) return;
            this.#disconnectObserver();
            this.#inView = true;
            this.#load();
        }), {
            rootMargin: "0px 0px 400px 0px"
        });
        this.#observer.observe(this.hidden ? this.parentElement : this);
    }
    disconnectedCallback() {
        this.#disconnectObserver();
        document.removeEventListener("visitorConsentCollected", this.#onConsentCollected);
    }
    #disconnectObserver() {
        this.#observer?.disconnect();
        this.#observer = null;
    }
    async #load() {
        if (this.dataset.loaded || this.#loading) return;
        this.#loading = true;
        this.#recheck = false;
        try {
            const allowed = await preferencesAllowed();
            if (allowed !== true) {
                if (allowed === false) clearHandles();
                return;
            }
            this.dataset.loaded = "true";
            document.removeEventListener("visitorConsentCollected", this.#onConsentCollected);
            const limit = Number(this.dataset.limit) || 8;
            // Request every stored handle (max 20, the all_products lookup limit) so deleted or
            // unpublished products are skipped in favour of older valid ones, then trim to the limit.
            const handles = readHandles().filter((handle => handle !== this.dataset.currentHandle)).slice(0, MAX_HANDLES);
            if (!handles.length) return;
            const result = await this.#fetchCards(handles);
            if (!result) return;
            const {cards, checked} = result;
            // Only prune handles the section actually looked up and found no product for.
            pruneHandles(handles.filter((handle => checked.has(handle) && !cards.has(handle))));
            if (!cards.size) return;
            this.#render(Array.from(cards.values()).slice(0, limit));
        } finally {
            this.#loading = false;
            if (this.#recheck && !this.dataset.loaded) this.#load();
        }
    }
    // Resolves to {cards, checked}: a Map of handle => card for the products that still exist, and the
    // set of handles the section looked up. Null if the request failed or did not return the cards section
    // (e.g. a password page or a search redirect), so nothing is pruned on a bad response.
    async #fetchCards(handles) {
        try {
            const params = new URLSearchParams({
                q: handles.join(" "),
                type: "product",
                section_id: CARDS_SECTION_ID
            });
            const response = await fetch(`${this.dataset.searchUrl}?${params}`);
            if (!response.ok) return null;
            const html = document.createElement("div");
            html.innerHTML = await response.text();
            const container = html.querySelector("[data-recently-viewed-cards]");
            if (!container) return null;
            const checked = new Set((container.dataset.recentlyViewedChecked || "").split(" ").filter(Boolean));
            const cards = new Map;
            container.querySelectorAll("[data-recently-viewed-handle]").forEach((wrapper => {
                const card = wrapper.querySelector(".product-card");
                if (card) cards.set(wrapper.dataset.recentlyViewedHandle, card);
            }));
            return {
                cards,
                checked
            };
        } catch (e) {
            console.error(e);
            return null;
        }
    }
    #render(cards) {
        const grid = Number(this.dataset.grid);
        const gridMobile = Number(this.dataset.gridMobile);
        const fragment = this.querySelector("[data-recently-viewed-template]").content.cloneNode(true);
        const itemsScroll = fragment.querySelector("items-scroll-paged-navigation");
        const items = fragment.querySelector("[data-items]");
        if (cards.length > grid) {
            itemsScroll.setAttribute("items-overflow", "");
        } else if (cards.length > gridMobile) {
            itemsScroll.setAttribute("items-overflow-max", "phab");
        }
        if (grid > cards.length) items.classList.add("grid-layout--align-center");
        cards.forEach((card => {
            if (gridMobile !== 2) {
                card.querySelectorAll(".c-btn--small\\@mobile").forEach((btn => btn.classList.remove("c-btn--small@mobile")));
            }
            items.appendChild(card);
        }));
        this.querySelector("[data-recently-viewed-target]").replaceChildren(fragment);
        this.removeAttribute("hidden");
        PageAnimations.setup(this);
    }
}

if (!customElements.get("recently-viewed-products")) {
    customElements.define("recently-viewed-products", RecentlyViewedProducts);
}
